import {
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  ModalBody,
  ModalContent,
  ModalHeader,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ChainFullIds,
  ChainIds,
  getChainPriority,
  getNativeTokenPriority,
  getTokenSymbol,
  isMultichainToken,
  useChainRegistry,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import {
  type UnifiedBalanceWithPrices,
  useGetNetworkByFullId,
  useGetNetworkChain,
  useGetTokensAndBalances,
  useSelectedNetwork,
} from "@umami/state";
import { ZEROX_ETH_ADDRESS, getTokenAddress } from "@umami/swap";
import { type ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { ChainIcon } from "../../Token/ChainIcon";
import { TokenMultichain } from "../../Token/Token";

import { AllNetworksIcon, ChevronDownIcon, SearchIcon } from "@/assets/icons";
import { ModalBackButton } from "@/components/BackButton";
import { type TokenWithAddress, fetchPopularTokens, searchTokens } from "@/services/tokenService";
import { useColor } from "@/styles/useColor";

type Network = "All networks" | "Ethereum" | "Etherlink" | "BSC" | "Tezos";

interface NetworkConfig {
  id: Network;
  label: string;
  icon: ReactElement;
}

const NETWORK_CONFIGS: NetworkConfig[] = [
  {
    id: "All networks",
    label: "All networks",
    icon: <AllNetworksIcon width="20px" height="20px" />,
  },
  {
    id: "Tezos",
    label: "Tezos",
    icon: <ChainIcon boxSize="20px" chainId={ChainIds.TEZOS} />,
  },
  {
    id: "Ethereum",
    label: "Ethereum",
    icon: <ChainIcon boxSize="20px" chainId={ChainIds.ETHEREUM} />,
  },
  {
    id: "Etherlink",
    label: "Etherlink",
    icon: <ChainIcon boxSize="20px" chainId={ChainIds.ETHERLINK} />,
  },
];

interface AssetSelectModalProps {
  mode: "buy" | "sell";
  sellToken?: UnifiedBalanceWithPrices;
  buyToken?: UnifiedBalanceWithPrices;
}

export const AssetSelectModal = ({ mode, sellToken, buyToken }: AssetSelectModalProps) => {
  const { goBack } = useDynamicModalContext();
  const { balances, tokens } = useGetTokensAndBalances();
  const [searchQuery, setSearchQuery] = useState("");
  const [dynamicTokens, setDynamicTokens] = useState<TokenWithAddress[]>([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [resolvedIconUrls, setResolvedIconUrls] = useState<Map<string, string>>(new Map());
  const color = useColor();
  const currentNetwork = useSelectedNetwork();
  const { registry } = useChainRegistry();
  const getNetworkChain = useGetNetworkChain();
  const getNetworkConfig = useGetNetworkByFullId();

  // Always start with "All Networks"
  const [selectedNetwork, setSelectedNetwork] = useState<Network>("All networks");

  useEffect(() => {
    const loadDynamicTokens = async () => {
      if (
        selectedNetwork === "All networks" ||
        selectedNetwork === "Ethereum" ||
        selectedNetwork === "Etherlink"
      ) {
        setIsLoadingTokens(true);
        try {
          const tokens: TokenWithAddress[] = [];

          if (selectedNetwork === "All networks" || selectedNetwork === "Ethereum") {
            const ethereumTokens = await fetchPopularTokens(ChainFullIds.ETHEREUM);
            tokens.push(...ethereumTokens);
          }

          if (selectedNetwork === "All networks" || selectedNetwork === "Etherlink") {
            const etherlinkTokens = await fetchPopularTokens(ChainFullIds.ETHERLINK);
            tokens.push(...etherlinkTokens);
          }

          setDynamicTokens(tokens);
        } catch (error) {
          console.error("Error loading dynamic tokens:", error);
        } finally {
          setIsLoadingTokens(false);
        }
      } else {
        setDynamicTokens([]);
      }
    };

    void loadDynamicTokens();
  }, [selectedNetwork]);

  // Search tokens dynamically when user types
  useEffect(() => {
    const searchDynamicTokens = async () => {
      if (
        searchQuery.length >= 2 &&
        (selectedNetwork === "All networks" ||
          selectedNetwork === "Ethereum" ||
          selectedNetwork === "Etherlink")
      ) {
        setIsLoadingTokens(true);
        try {
          const searchResults: TokenWithAddress[] = [];

          if (selectedNetwork === "All networks" || selectedNetwork === "Ethereum") {
            const ethereumResults = await searchTokens(searchQuery, ChainFullIds.ETHEREUM);
            searchResults.push(...ethereumResults);
          }

          if (selectedNetwork === "All networks" || selectedNetwork === "Etherlink") {
            const etherlinkResults = await searchTokens(searchQuery, ChainFullIds.ETHERLINK);
            searchResults.push(...etherlinkResults);
          }

          setDynamicTokens(searchResults);
        } catch (error) {
          console.error("Error searching tokens:", error);
        } finally {
          setIsLoadingTokens(false);
        }
      } else if (
        searchQuery.length === 0 &&
        (selectedNetwork === "All networks" ||
          selectedNetwork === "Ethereum" ||
          selectedNetwork === "Etherlink")
      ) {
        setIsLoadingTokens(true);
        try {
          const tokens: TokenWithAddress[] = [];

          if (selectedNetwork === "All networks" || selectedNetwork === "Ethereum") {
            const ethereumTokens = await fetchPopularTokens(ChainFullIds.ETHEREUM);
            tokens.push(...ethereumTokens);
          }

          if (selectedNetwork === "All networks" || selectedNetwork === "Etherlink") {
            const etherlinkTokens = await fetchPopularTokens(ChainFullIds.ETHERLINK);
            tokens.push(...etherlinkTokens);
          }

          setDynamicTokens(tokens);
        } catch (error) {
          console.error("Error loading popular tokens:", error);
        } finally {
          setIsLoadingTokens(false);
        }
      }
    };

    const debounceTimer = setTimeout(() => {
      void searchDynamicTokens();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, selectedNetwork]);

  // Function to resolve icon URLs for Etherlink tokens
  const resolveEtherlinkIconUrl = useCallback(
    async (token: TokenWithAddress) => {
      if (
        getNetworkChain(token.chainId) === ChainIds.ETHERLINK &&
        token.address &&
        !resolvedIconUrls.has(token.address)
      ) {
        try {
          const provider = registry.getChainProvider(token.chainId);

          const iconUrl = await provider.getAddressIconUrl(token.address);

          if (iconUrl) {
            setResolvedIconUrls(prev => new Map(prev).set(token.address!, iconUrl));
          }
        } catch (error) {
          console.error(`Error resolving icon for ${token.symbol} on Etherlink:`, error);
        }
      }
    },
    [resolvedIconUrls, registry, getNetworkChain]
  );

  // Resolve icons for Etherlink tokens when dynamic tokens change
  useEffect(() => {
    const resolveIcons = async () => {
      const etherlinkTokens = dynamicTokens.filter(
        token =>
          getNetworkChain(token.chainId) === ChainIds.ETHERLINK &&
          token.address &&
          !resolvedIconUrls.has(token.address)
      );

      for (const token of etherlinkTokens) {
        await resolveEtherlinkIconUrl(token);
      }
    };

    void resolveIcons();
  }, [dynamicTokens, resolvedIconUrls, registry, resolveEtherlinkIconUrl, getNetworkChain]);

  // Combine user balances with dynamic tokens
  const allTokens = useMemo(() => {
    const unifiedTokens: UnifiedBalanceWithPrices[] = tokens
      .map(token => {
        const symbol = getTokenSymbol(token);

        if (!symbol) {
          return null;
        }

        return {
          chainId: token.chainId,
          symbol,
          balance: token.balance,
          usdPrice: token.usdPrice,
          totalUsdPrice: token.totalUsdPrice,
          ...(isMultichainToken(token) && {
            address: token.address,
            logoURI: token.iconUrl,
            decimals: token.decimals,
          }),
        };
      })
      .filter((token): token is UnifiedBalanceWithPrices => token !== null);

    // Create a map of user tokens for quick lookup
    const userTokenMap = new Map(
      [...balances, ...unifiedTokens].map(b => [`${b.chainId}-${b.symbol}`, b])
    );

    // Merge user tokens with dynamic tokens, prioritizing user balances
    const mergedTokens = dynamicTokens.map(token => {
      const key = `${token.chainId}-${token.symbol}`;
      const userToken = userTokenMap.get(key);

      if (userToken) {
        // If user has this token, use their values but keep the address and logoURI for icon
        return {
          ...token,
          balance: userToken.balance,
          usdPrice: userToken.usdPrice,
          totalUsdPrice: userToken.totalUsdPrice,
        };
      }

      // If user doesn't have this token, keep dynamic values
      return token;
    });

    // Add any user tokens that aren't in dynamic tokens
    const dynamicTokenKeys = new Set(dynamicTokens.map(t => `${t.chainId}-${t.symbol}`));
    const additionalUserTokens = [...balances, ...unifiedTokens].filter(
      b => !dynamicTokenKeys.has(`${b.chainId}-${b.symbol}`)
    );

    const result = [...mergedTokens, ...additionalUserTokens];

    result.sort((a, b) => {
      // First, prioritize tokens with balances
      const hasBalanceA = Number(a.totalUsdPrice) > 0;
      const hasBalanceB = Number(b.totalUsdPrice) > 0;

      if (hasBalanceA !== hasBalanceB) {
        return hasBalanceA ? -1 : 1;
      }

      // If both have balances, sort by balance amount
      if (hasBalanceA && hasBalanceB) {
        const balanceA = Number(a.totalUsdPrice);
        const balanceB = Number(b.totalUsdPrice);

        if (balanceA !== balanceB) {
          return balanceB - balanceA;
        }
      }

      // Then apply chain priority logic
      const priorityA = getChainPriority(getNetworkChain(a.chainId));
      const priorityB = getChainPriority(getNetworkChain(b.chainId));

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Within the same chain, prioritize native tokens
      const nativePriorityA = getNativeTokenPriority(getNetworkChain(a.chainId), a.symbol);
      const nativePriorityB = getNativeTokenPriority(getNetworkChain(b.chainId), b.symbol);

      if (nativePriorityA !== nativePriorityB) {
        return nativePriorityA - nativePriorityB;
      }

      return a.symbol.localeCompare(b.symbol);
    });

    return result;
  }, [balances, tokens, dynamicTokens, getNetworkChain]);

  const filteredTokens = useMemo(() => {
    // First filter by search query and selected network
    let tokens = allTokens.filter(token => {
      const matchesSearch = token.symbol.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesNetwork =
        selectedNetwork === "All networks" ||
        (selectedNetwork === "Ethereum" && getNetworkChain(token.chainId) === ChainIds.ETHEREUM) ||
        (selectedNetwork === "Etherlink" &&
          getNetworkChain(token.chainId) === ChainIds.ETHERLINK) ||
        (selectedNetwork === "Tezos" && getNetworkChain(token.chainId) === ChainIds.TEZOS);

      // For Tezos network, only show XTZ token
      if (getNetworkChain(token.chainId) === ChainIds.TEZOS) {
        return matchesSearch && matchesNetwork && token.symbol === "XTZ";
      }

      return matchesSearch && matchesNetwork;
    });

    // Get the reference token based on mode
    const referenceToken = mode === "buy" ? sellToken : buyToken;

    // Only apply filtering if the referenceToken is sellToken (mode === "buy")
    if (referenceToken && mode === "buy") {
      // Always filter out the token that's already selected (same symbol and chain)
      tokens = tokens.filter(
        token =>
          !(token.symbol === referenceToken.symbol && token.chainId === referenceToken.chainId)
      );

      // For Etherlink and Ethereum tokens, allow any cross-chain swaps
      if (
        getNetworkChain(referenceToken.chainId) === ChainIds.ETHERLINK ||
        getNetworkChain(referenceToken.chainId) === ChainIds.ETHEREUM
      ) {
        // Show all Etherlink and Ethereum tokens (except the one already selected)
        tokens = tokens.filter(token => {
          // If referenceToken is Etherlink, allow EVM and Tezos tokens
          if (getNetworkChain(referenceToken.chainId) === ChainIds.ETHERLINK) {
            return (
              (getNetworkChain(token.chainId) === ChainIds.ETHERLINK ||
                getNetworkChain(token.chainId) === ChainIds.ETHEREUM ||
                getNetworkChain(token.chainId) === ChainIds.TEZOS) &&
              !(token.symbol === referenceToken.symbol && token.chainId === referenceToken.chainId)
            );
          }

          // Otherwise, only EVM tokens
          return (
            (getNetworkChain(token.chainId) === ChainIds.ETHERLINK ||
              getNetworkChain(token.chainId) === ChainIds.ETHEREUM) &&
            !(token.symbol === referenceToken.symbol && token.chainId === referenceToken.chainId)
          );
        });
      } else {
        // For other chains (like Tezos), keep the existing logic
        // Show the same token on other networks
        const sameTokenOtherNetwork = tokens.filter(
          token =>
            token.symbol === referenceToken.symbol && token.chainId !== referenceToken.chainId
        );
        tokens = [...sameTokenOtherNetwork];
      }
    }

    return tokens;
  }, [allTokens, searchQuery, selectedNetwork, mode, sellToken, buyToken, getNetworkChain]);

  const handleClick = (asset: UnifiedBalanceWithPrices) => {
    const network = currentNetwork.name === "mainnet" ? "mainnet" : "shadownet";
    const chain =
      getNetworkChain(asset.chainId) === ChainIds.ETHEREUM
        ? "ethereum"
        : getNetworkChain(asset.chainId) === ChainIds.ETHERLINK
          ? "etherlink"
          : getNetworkChain(asset.chainId) === ChainIds.BSC
            ? "bsc"
            : "tezos";

    // For dynamic tokens, use the stored address
    let finalTokenAddress = "";

    const assetWithAddress = asset as TokenWithAddress;

    if (assetWithAddress.address && typeof assetWithAddress.address === "string") {
      finalTokenAddress = assetWithAddress.address;
    } else {
      const tokenAddress = getTokenAddress(asset.symbol, network, chain);

      if (
        asset.symbol === "XTZ" &&
        (getNetworkChain(asset.chainId) === ChainIds.TEZOS ||
          getNetworkChain(asset.chainId) === ChainIds.ETHERLINK)
      ) {
        finalTokenAddress = "XTZ";
      } else if (asset.symbol === "ETH" && getNetworkChain(asset.chainId) === ChainIds.ETHEREUM) {
        finalTokenAddress = ZEROX_ETH_ADDRESS;
      } else if (tokenAddress && tokenAddress !== "") {
        finalTokenAddress = tokenAddress;
      }
    }

    if (!finalTokenAddress) {
      console.error("No token address available for:", asset);

      return;
    }

    let iconUrl = assetWithAddress.logoURI;

    if (getNetworkChain(asset.chainId) === ChainIds.ETHERLINK && assetWithAddress.address) {
      iconUrl = resolvedIconUrls.get(assetWithAddress.address) || assetWithAddress.logoURI;
    }

    if (mode === "sell") {
      goBack({
        sellToken: { ...asset, address: finalTokenAddress, iconUrl },
        mode,
        buyToken: undefined,
        buyAmount: undefined,
      });
    } else {
      goBack({
        buyToken: { ...asset, address: finalTokenAddress, iconUrl },
        mode,
        buyAmount: undefined,
      });
    }
  };

  return (
    <ModalContent>
      <ModalHeader>
        <ModalBackButton />
        <Center flexDirection="column" gap="12px">
          <Heading size="xl">Select Asset</Heading>
        </Center>
      </ModalHeader>

      <ModalBody>
        <InputGroup marginBottom="4">
          <InputLeftElement paddingLeft="16px">
            <Icon as={SearchIcon} width="24px" height="24px" color={color("400")} />
          </InputLeftElement>
          <Input
            paddingStart="52px !important"
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search..."
            value={searchQuery}
          />
          <InputRightElement width="auto" paddingRight="2">
            <Menu>
              {({ onClose, isOpen }) => (
                <>
                  <MenuButton
                    as={Button}
                    minWidth="120px"
                    height="fit-content"
                    fontWeight="400"
                    lineHeight="30px"
                    border="0"
                    _hover={{ bg: isOpen ? "gray.100" : "gray.50" }}
                    backgroundColor={isOpen ? "gray.100 !important" : "white"}
                    leftIcon={NETWORK_CONFIGS.find(n => n.id === selectedNetwork)?.icon}
                    rightIcon={<ChevronDownIcon width="20px" height="20px" />}
                  >
                    {selectedNetwork}
                  </MenuButton>
                  <MenuList
                    marginTop="-5px"
                    padding="3"
                    borderColor={color("100")}
                    borderRadius="30px"
                    backgroundColor={color("white")}
                  >
                    <RadioGroup
                      onChange={value => {
                        setSelectedNetwork(value as Network);
                        onClose();
                      }}
                      value={selectedNetwork}
                    >
                      <Stack spacing="2">
                        {NETWORK_CONFIGS.map(network => (
                          <Flex
                            key={network.id}
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                            padding="2"
                            background={
                              selectedNetwork === network.id ? color("100") : "transparent"
                            }
                            borderRadius="100px"
                            _hover={{ bg: "gray.50" }}
                            cursor="pointer"
                            onClick={() => {
                              setSelectedNetwork(network.id);
                              onClose();
                            }}
                          >
                            <Flex alignItems="center" gap="2">
                              {network.icon}
                              <Text fontSize="16px" fontWeight="600">
                                {network.label}
                              </Text>
                            </Flex>
                            <Radio isReadOnly value={network.id} />
                          </Flex>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </MenuList>
                </>
              )}
            </Menu>
          </InputRightElement>
        </InputGroup>
        <VStack gap="12px" overflowY="auto" maxHeight="400px">
          {isLoadingTokens ? (
            <Center padding="40px">
              <Spinner color={color("500")} size="lg" />
            </Center>
          ) : (
            filteredTokens.map(token => {
              const networkConfig = getNetworkConfig(token.chainId);

              if (networkConfig.isTestnet) {
                return null;
              }

              const tokenWithAddress = token as TokenWithAddress;

              let iconUrl = tokenWithAddress.logoURI;

              if (
                getNetworkChain(token.chainId) === ChainIds.ETHERLINK &&
                tokenWithAddress.address
              ) {
                iconUrl = resolvedIconUrls.get(tokenWithAddress.address);
              }

              return (
                <TokenMultichain
                  key={`${token.chainId}-${tokenWithAddress.address}`}
                  padding="12px"
                  balance={Number(token.balance)}
                  chain={token.chainId}
                  data-testid="multichain-token"
                  iconUrl={iconUrl}
                  name={token.symbol}
                  onClick={() => handleClick(token)}
                  showBalances={Number(token.balance) !== 0}
                  usdBalance={token.totalUsdPrice}
                  usdPrice={token.usdPrice}
                />
              );
            })
          )}
        </VStack>
      </ModalBody>
    </ModalContent>
  );
};
